/**
 * Organizer Controls Component
 *
 * Settings panel for room organizers to manage mode controls,
 * participant management, and room actions.
 */

import { useState } from 'react'
import { clsx } from 'clsx'
import type {
  RoomMeta,
  Participant,
  Role,
  MicMode,
  ChatMode,
  POIMode,
  TransportType,
  HierarchyRoomType,
  SectorInfo,
} from '@/lib/types'
import { GeofenceDot } from './GeofenceStatus'
import { ShareRoom } from '@/components/common/ShareRoom'

interface OrganizerControlsProps {
  roomMeta: RoomMeta
  roomId: string
  roomSecret: string
  roomPin?: string
  participants: Participant[]
  myRole: Role
  currentLocation?: { latitude: number; longitude: number } | null
  onUpdateMeta: (updates: Partial<RoomMeta>) => void
  onUpdateParticipantRole: (peerId: string, role: Role) => void
  onKickParticipant: (peerId: string) => void
  onEndRoom: () => void
  className?: string
  // Hierarchy props
  hierarchyType?: HierarchyRoomType
  sectors?: SectorInfo[]
  onEnableCommandMode?: () => void
}

export function OrganizerControls({
  roomMeta,
  roomId,
  roomSecret,
  roomPin,
  participants,
  myRole,
  currentLocation,
  onUpdateMeta,
  onUpdateParticipantRole,
  onKickParticipant,
  onEndRoom,
  className,
  // Hierarchy props
  hierarchyType = 'standalone',
  sectors = [],
  onEnableCommandMode,
}: OrganizerControlsProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [showEndConfirm, setShowEndConfirm] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  const isOrganizer = myRole === 'organizer'
  const isCoordinator = myRole === 'coordinator' || isOrganizer

  if (!isCoordinator) return null

  return (
    <div className={clsx('glass-panel rounded-lg border border-white/10 overflow-hidden', className)}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">⚙️</span>
          <span className="font-bold text-xs uppercase tracking-widest text-primary">Room Controls</span>
          {isOrganizer && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30 font-mono">
              Admin
            </span>
          )}
        </div>
        <span className="text-white/40 text-[10px] font-mono">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {/* Controls panel - scrollable when expanded */}
      {isExpanded && (
        <div className="p-3 space-y-4 border-t border-white/10 max-h-[60vh] overflow-y-auto">
          {/* Mode controls (organizer only) */}
          {isOrganizer && (
            <>
              {/* Mic Mode */}
              <div>
                <label className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2 block">Who Can Speak</label>
                <ModeSelector
                  options={[
                    { value: 'open', label: 'Everyone' },
                    { value: 'coordinators_only', label: 'Mods Only' },
                    { value: 'organizer_only', label: 'Admin Only' },
                  ]}
                  value={roomMeta.micMode}
                  onChange={(value) => onUpdateMeta({ micMode: value as MicMode })}
                />
              </div>

              {/* Chat Mode */}
              <div>
                <label className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2 block">Chat Settings</label>
                <ModeSelector
                  options={[
                    { value: 'open', label: 'Open' },
                    { value: 'slow_mode', label: 'Slow Mode' },
                    { value: 'coordinators_only', label: 'Mods Only' },
                  ]}
                  value={roomMeta.chatMode}
                  onChange={(value) => onUpdateMeta({ chatMode: value as ChatMode })}
                />
              </div>

              {/* POI Mode */}
              <div>
                <label className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2 block">Location Reports</label>
                <ModeSelector
                  options={[
                    { value: 'open', label: 'Everyone' },
                    { value: 'coordinators_only', label: 'Mods Only' },
                    { value: 'organizer_only', label: 'Admin Only' },
                    { value: 'disabled', label: 'Off' },
                  ]}
                  value={roomMeta.poiMode}
                  onChange={(value) => onUpdateMeta({ poiMode: value as POIMode })}
                />
              </div>

              {/* POI Expiration */}
              <div>
                <label className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2 block">Report Duration</label>
                <ModeSelector
                  options={[
                    { value: '0', label: 'Forever' },
                    { value: '5', label: '5 min' },
                    { value: '10', label: '10 min' },
                    { value: '15', label: '15 min' },
                    { value: '30', label: '30 min' },
                    { value: '60', label: '1 hour' },
                  ]}
                  value={String(roomMeta.poiExpirationMinutes)}
                  onChange={(value) =>
                    onUpdateMeta({ poiExpirationMinutes: parseInt(value, 10) })
                  }
                />
              </div>

              {/* Sharing Controls */}
              <div>
                <label className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2 block">Access Control</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => onUpdateMeta({ sharingEnabled: !roomMeta.sharingEnabled })}
                    className={clsx(
                      'flex-1 py-2 rounded font-bold uppercase tracking-wider text-[10px] transition-colors flex items-center justify-center gap-2 border',
                      roomMeta.sharingEnabled
                        ? 'bg-green-500/20 text-green-400 border-green-500/50 hover:bg-green-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/50 hover:bg-red-500/30'
                    )}
                  >
                    {roomMeta.sharingEnabled ? '🔓 OPEN' : '🔒 LOCKED'}
                  </button>
                  <button
                    onClick={() => {
                      setShowShareModal(true)
                    }}
                    disabled={!roomMeta.sharingEnabled}
                    className={clsx(
                      'flex-1 py-2 rounded font-bold uppercase tracking-wider text-[10px] transition-colors flex items-center justify-center gap-2 border',
                      roomMeta.sharingEnabled
                        ? 'bg-teal/20 text-teal border-teal/50 hover:bg-teal/30'
                        : 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed'
                    )}
                  >
                    Share Room
                  </button>
                </div>
              </div>

              {/* Geofence Recenter */}
              <div>
                <label className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2 block">Room Location</label>
                <button
                  onClick={() => {
                    if (currentLocation) {
                      onUpdateMeta({
                        center: {
                          latitude: currentLocation.latitude,
                          longitude: currentLocation.longitude,
                        },
                      })
                    }
                  }}
                  disabled={!currentLocation}
                  className={clsx(
                    'w-full py-2 rounded font-bold uppercase tracking-wider text-[10px] transition-colors flex items-center justify-center gap-2 border',
                    currentLocation
                      ? 'bg-blue-500/20 text-blue-400 border-blue-500/50 hover:bg-blue-500/30'
                      : 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed'
                  )}
                >
                  📍 Recenter to My Location
                </button>
                <p className="text-[9px] text-white/40 mt-1 font-mono">
                  Move the room boundary to your current location
                </p>
              </div>

              {/* Command Mode Section
                  NOTE: This feature has documented concerns regarding response times,
                  self-organization vs. command structures, and philosophical alignment
                  with decentralized activist groups. See docs/feasibility-room-merge-hierarchy.md
                  for full context and community feedback status. */}
              <div>
                <label className="text-[10px] text-primary uppercase tracking-wider font-bold mb-2 block">
                  📡 Command Mode (ICS)
                </label>
                {hierarchyType === 'standalone' ? (
                  // Standalone room - show button to enable command mode
                  <div>
                    <button
                      onClick={onEnableCommandMode}
                      disabled={!onEnableCommandMode}
                      className={clsx(
                        'w-full py-2 rounded font-bold uppercase tracking-wider text-[10px] transition-colors flex items-center justify-center gap-2 border',
                        onEnableCommandMode
                          ? 'bg-purple-500/20 text-purple-400 border-purple-500/50 hover:bg-purple-500/30'
                          : 'bg-white/5 text-white/30 border-white/10 cursor-not-allowed'
                      )}
                    >
                      👑 Enable Command Center
                    </button>
                    <p className="text-[9px] text-white/40 mt-1 font-mono">
                      Convert this room into a command center with sector management
                    </p>
                  </div>
                ) : hierarchyType === 'command' ? (
                  <div className="flex items-center gap-2 p-2 rounded bg-purple-500/10 border border-purple-500/30">
                    <span className="text-lg">👑</span>
                    <div>
                      <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Command Room Active</span>
                      <p className="text-[9px] text-white/40 font-mono mt-0.5">
                        {sectors.length} sector{sectors.length !== 1 ? 's' : ''} • Manage in Command Dashboard above
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-2 rounded bg-teal/10 border border-teal/30">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📍</span>
                      <span className="text-xs font-bold text-teal uppercase tracking-wider">Sector Room</span>
                    </div>
                    <p className="text-[9px] text-white/40 font-mono mt-1">
                      This room is part of a command hierarchy
                    </p>
                  </div>
                )}
                <hr className="border-white/10 mt-4" />
              </div>

              <hr className="border-white/10" />
            </>
          )}

          {/* Participants */}
          <div>
            <label className="text-[10px] text-white/60 uppercase tracking-wider font-bold mb-2 block">
              People ({participants.length})
            </label>
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {participants.map((participant) => (
                <ParticipantRow
                  key={participant.peerId}
                  participant={participant}
                  isOrganizer={isOrganizer}
                  onChangeRole={(role) =>
                    onUpdateParticipantRole(participant.peerId, role)
                  }
                  onKick={() => onKickParticipant(participant.peerId)}
                />
              ))}
            </div>
          </div>

          {/* Room actions (organizer only) */}
          {isOrganizer && (
            <>
              <hr className="border-white/10" />

              <div className="flex gap-2">
                {/* Pause/Resume */}
                <button
                  onClick={() => onUpdateMeta({ isPaused: !roomMeta.isPaused })}
                  className={clsx(
                    'flex-1 py-2 rounded font-bold uppercase tracking-wider text-[10px] transition-colors border',
                    roomMeta.isPaused
                      ? 'bg-green-500/20 text-green-400 border-green-500/50 hover:bg-green-500/30'
                      : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50 hover:bg-yellow-500/30'
                  )}
                >
                  {roomMeta.isPaused ? '▶ Resume' : '⏸ Pause'}
                </button>

                {/* End Room */}
                {!showEndConfirm ? (
                  <button
                    onClick={() => setShowEndConfirm(true)}
                    className="flex-1 py-2 rounded font-bold uppercase tracking-wider text-[10px] bg-red-500/20 text-red-400 border border-red-500/50 hover:bg-red-500/30 transition-colors"
                  >
                    End Room
                  </button>
                ) : (
                  <div className="flex-1 flex gap-1">
                    <button
                      onClick={() => {
                        onEndRoom()
                        setShowEndConfirm(false)
                      }}
                      className="flex-1 py-2 rounded font-bold uppercase tracking-wider text-[10px] bg-red-500 text-white border border-red-600 hover:bg-red-600 transition-colors"
                    >
                      CONFIRM
                    </button>
                    <button
                      onClick={() => setShowEndConfirm(false)}
                      className="flex-1 py-2 rounded font-bold uppercase tracking-wider text-[10px] bg-white/10 text-white/60 border border-white/20 hover:bg-white/20 transition-colors"
                    >
                      CANCEL
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Share Room Modal */}
      {showShareModal && (
        <ShareRoom
          key={`share-${roomId}`}
          roomId={roomId}
          roomSecret={roomSecret}
          roomName={roomMeta.name}
          pin={roomPin}
          isModal
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  )
}

/**
 * Mode selector toggle buttons
 */
function ModeSelector({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex flex-wrap gap-1 bg-black/40 p-1 rounded border border-white/10">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={clsx(
            'px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider transition-all flex-1',
            value === option.value
              ? 'bg-primary/20 text-primary border border-primary/50 shadow-[0_0_10px_rgba(242,13,128,0.2)]'
              : 'text-white/40 hover:text-white/60 hover:bg-white/5 border border-transparent'
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Participant row with role management
 */
function ParticipantRow({
  participant,
  isOrganizer,
  onChangeRole,
  onKick,
}: {
  participant: Participant
  isOrganizer: boolean
  onChangeRole: (role: Role) => void
  onKick: () => void
}) {
  const [showActions, setShowActions] = useState(false)

  const roleColors = {
    organizer: 'bg-primary/20 text-primary border-primary/30',
    coordinator: 'bg-teal/20 text-teal border-teal/30',
    participant: 'bg-white/5 text-white/40 border-white/10',
  }

  // Connection type indicator configuration
  const connectionTypeConfig: Record<TransportType, { icon: string; label: string; color: string }> = {
    webrtc: { icon: '🌐', label: 'P2P', color: 'text-green-400' },
    websocket: { icon: '📡', label: 'Relay', color: 'text-yellow-400' },
    bluetooth: { icon: '📶', label: 'BT', color: 'text-blue-400' },
    lora: { icon: '📻', label: 'LoRa', color: 'text-purple-400' },
  }

  const connectionInfo = participant.connectionType
    ? connectionTypeConfig[participant.connectionType]
    : null

  return (
    <div className="flex items-center justify-between p-2 rounded border border-white/5 bg-black/20 hover:bg-black/40 transition-colors">
      <div className="flex items-center gap-2">
        <GeofenceDot isInside={participant.isInsideGeofence} />
        <span className="text-xs font-mono text-white/80 uppercase">{participant.displayName}</span>
        <span
          className={clsx(
            'text-[9px] px-1 py-px rounded border font-bold uppercase tracking-wider',
            roleColors[participant.role]
          )}
        >
          {participant.role === 'organizer'
            ? 'Admin'
            : participant.role === 'coordinator'
              ? 'Mod'
              : 'Member'}
        </span>
        {/* Connection type badge */}
        {connectionInfo && (
          <span
            className={clsx(
              'text-[9px] px-1 py-px rounded bg-black/40 border border-white/10 font-mono',
              connectionInfo.color
            )}
            title={`Connected via ${connectionInfo.label}`}
          >
            {connectionInfo.icon}
          </span>
        )}
        {participant.isMuted && (
          <span className="text-[10px] text-red-500">🔇</span>
        )}
      </div>

      {isOrganizer && participant.role !== 'organizer' && (
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="text-white/20 hover:text-white px-2 font-mono text-xs"
          >
            [::]
          </button>

          {showActions && (
            <div className="absolute right-0 top-full mt-1 bg-black border border-white/20 rounded shadow-[0_0_20px_rgba(0,0,0,0.8)] z-50 min-w-[140px] overflow-hidden">
              {participant.role === 'participant' && (
                <button
                  onClick={() => {
                    onChangeRole('coordinator')
                    setShowActions(false)
                  }}
                  className="w-full px-3 py-2 text-left text-[10px] uppercase font-bold tracking-wider hover:bg-white/10 transition-colors text-teal"
                >
                  Make Moderator
                </button>
              )}
              {participant.role === 'coordinator' && (
                <button
                  onClick={() => {
                    onChangeRole('participant')
                    setShowActions(false)
                  }}
                  className="w-full px-3 py-2 text-left text-[10px] uppercase font-bold tracking-wider hover:bg-white/10 transition-colors text-white/60"
                >
                  Remove Moderator
                </button>
              )}
              <button
                onClick={() => {
                  onKick()
                  setShowActions(false)
                }}
                className="w-full px-3 py-2 text-left text-[10px] uppercase font-bold tracking-wider text-red-400 hover:bg-red-500/20 transition-colors border-t border-white/10"
              >
                Remove from Room
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
